import React, {useState} from 'react';
import gql from 'graphql-tag';
import {useLocation} from 'react-router';
import {useQuery} from '@apollo/client';
import CardPaginator from '../card/CardPaginator';
import TitleNumber from '../card/TitleNumber';
import TitleCaption from '../card/TitleCaption';
import CardTitle from '../card/CardTitle';
import Card from '../card/Card';
import CardContent from '../card/CardContent';
import PriceFormatter from '../../../models/formatter/PriceFormatter';
import ExpiringLicenseRecord from './ExpiringLicenseRecord';
import {getNumberParam} from '../../../utils/Parameters';
import {License} from "../../../schema";

export default function ExpiringLicenses() {
  const QUERY_PARAM = 'licenses';
  const location = useLocation();
  const activePage = getNumberParam(location.search, QUERY_PARAM, 1);

  const count = 10;
  const [skip, setSkip] = useState((activePage - 1) * count);
  const [total, setTotal] = useState(0);

  const handlePageSwitch = (page: number) => {
    const newSkip = (page - 1) * count;
    setSkip(newSkip);
  };

  const calculatePage = () => Math.floor(skip / count) + 1;

  const initialLoading = () => loading && total === 0;

  const {data, loading} = useQuery<FeedData, LicenseFeedVars>(query, {
    variables: {
      skip, count
    },
    onCompleted: (feedData) => setTotal(feedData.licenses.total)
  });

  return (
    <Card loading={initialLoading()}>
      <CardTitle wide={true}>
        <TitleCaption>Expiring <br/>licenses</TitleCaption>
        <TitleNumber value={total}/>
      </CardTitle>
      <CardContent noBorders={false} loading={loading}>
        {data && total !== 0 && data.licenses.licenses.map((license) =>
          <ExpiringLicenseRecord key={license.id}
                                 licensee={license.licensee}
                                 expiresAt={license.expiresAt}
                                 amount={PriceFormatter.format({
                                   currency: license.currency,
                                   amount: license.amount
                                 })}
          />)}
        {total === 0 &&
          <div className="text-gray-dark">There are no expiring licenses</div>
        }
      </CardContent>
      {data && <CardPaginator total={total} page={calculatePage()} perPage={data.licenses.count}
                              className="px-5"
                              queryParamName={QUERY_PARAM}
                              onNextClick={handlePageSwitch} onPrevClick={handlePageSwitch}/>}
    </Card>
  );
}

interface LicenseFeed {
  total: number
  count: number
  skip: number
  licenses: License[]
}

interface FeedData {
  licenses: LicenseFeed
}

interface LicenseFeedVars {
  skip: number
  count: number
}

const query = gql`
  query ExpiringLicenses($skip: Int!, $count: Int!) {
    licenses: expiringLicenses(skip: $skip, count: $count) {
      total
      count
      skip

      licenses {
        id
        amount
        currency
        expiresAt
        expiresIn
        continuousRenewal
        licensee {
          id
          name
          type
        }
      }
    }
  }
`;
